import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabase";
import { C } from "../lib/constants";
import { uploadPublicFile } from "../lib/storage";
import { Btn } from "./ui/Btn";
import { Card } from "./ui/Card";
import { Field } from "./ui/Field";
import { TopNav } from "./ui/TopNav";
import { EmptyBox } from "./WorkFlow";

const buildClientePayload = (user, profile={}, extra={}) => {
  const nombre = extra.nombre ?? profile?.nombre ?? "";
  const telefono = String(extra.telefono ?? profile?.telefono ?? "").replace(/\D/g,"");
  const correo = extra.correo ?? extra.email ?? user?.email ?? profile?.email ?? profile?.correo ?? "";

  // FIX E3+E4: Solo enviar columnas que existen en la tabla clientes.
  // cancelaciones, reputacion, trabajos_activos, trabajos_finalizados son opcionales.
  // Se excluyen del payload base para evitar error si el SQL no fue ejecutado aun.
  return {
    user_id: user?.id || null,
    nombre,
    telefono,
    correo,
    email: correo,
    zona: extra.zona ?? profile?.zona ?? null,
    direccion: extra.direccion ?? profile?.direccion ?? null,
    foto_url: extra.foto_url ?? null,
    status: "activo",
    calificacion: extra.calificacion ?? 5,
    trabajos: extra.trabajos ?? 0,
    verificado: false,
    ultimo_acceso: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
};

const ensureClienteProfile = async (user, profile={}, extra={}) => {
  if (!user?.id) return null;

  const payload = buildClientePayload(user, profile, extra);

  const { data, error } = await supabase
    .from("clientes")
    .upsert([payload], { onConflict:"user_id" })
    .select()
    .single();

  if (error) {
    console.warn("Error creando/actualizando cliente:", error.message);
    return null;
  }

  return data;
};

export const InfoMini = ({label,value}) => (
  <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:14,padding:12,textAlign:"center"}}>
    <div style={{fontFamily:"Georgia, serif",fontSize:20,fontWeight:900,color:C.text}}>{value}</div>
    <div style={{color:C.muted,fontSize:11,fontWeight:800}}>{label}</div>
  </div>
);

export const PerfilCliente = ({setScreen, user, profile}) => {
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [correo, setCorreo] = useState(user?.email || "");
  const [zona, setZona] = useState("");
  const [direccion, setDireccion] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  // FIX EXTRA: estado para foto de perfil del cliente
  const [fotoActual, setFotoActual] = useState("");
  const [fotoFile, setFotoFile]     = useState(null);
  const [fotoPreview, setFotoPreview] = useState("");

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const cargar = useCallback(async () => {
    if (loadingRef.current) return;

    if (!user?.id) {
      if (mountedRef.current) setLoading(false);
      return;
    }

    loadingRef.current = true;
    if (mountedRef.current) {
      setLoading(true);
      setErr("");
    }

    try {
      const [clienteRes, perfilRes] = await Promise.all([
        supabase.from("clientes").select("*").eq("user_id", user.id).maybeSingle(),
        supabase.from("perfiles").select("nombre,telefono,correo").eq("user_id", user.id).neq("archivado", true).maybeSingle(),
      ]);

      if (clienteRes.error) throw clienteRes.error;

      let data = clienteRes.data;
      const p  = perfilRes.data || {};

      if (!data) {
        data = await ensureClienteProfile(user, {});
      }

      if (data && mountedRef.current) {
        setCliente(data);
        setNombre(p.nombre || data.nombre || "");
        setTelefono(String(p.telefono || data.telefono || "").replace(/\D/g,""));
        setCorreo(data.correo || data.email || p.correo || user.email || "");
        setZona(data.zona || "");
        setDireccion(data.direccion || "");
        const fotoUrl = data.foto_url || data.avatar || "";
        setFotoActual(fotoUrl);
        setFotoPreview(fotoUrl);
      }
    } catch (error) {
      console.warn("Error cargando perfil cliente:", error?.message || error);
      if (mountedRef.current) setErr(error?.message || "No se pudo cargar el perfil de cliente.");
    } finally {
      loadingRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardar = async () => {
    setMsg("");
    setErr("");

    if (!user?.id) {
      setErr("Necesitas iniciar sesión.");
      return;
    }

    if (!nombre.trim() || !telefono.trim() || !correo.trim()) {
      setErr("Nombre, WhatsApp y correo son obligatorios.");
      return;
    }

    const cleanPhone = telefono.replace(/\D/g,"");
    setSaving(true);
    try {
      // FIX EXTRA: subir foto nueva si el usuario la cambió
      let nuevaFotoUrl = fotoActual || "";
      if (fotoFile instanceof File) {
        const uploadedUrl = await uploadPublicFile(fotoFile, "fotos-perfil", user.id + "-perfil");
        if (uploadedUrl) {
          nuevaFotoUrl = uploadedUrl;
          setFotoActual(uploadedUrl);
          setFotoPreview(uploadedUrl);
        }
      }

      const payload = buildClientePayload(user, profile || {}, {
        nombre: nombre.trim(),
        telefono: cleanPhone,
        correo: correo.trim(),
        zona: zona.trim() || null,
        direccion: direccion.trim() || null,
        foto_url: nuevaFotoUrl || null,
      });

      const { data, error } = await supabase
        .from("clientes")
        .upsert([payload], { onConflict:"user_id" })
        .select()
        .maybeSingle();

      if (error) throw error;

      if (data) { setCliente(data); setFotoFile(null); }
      setMsg("Perfil de cliente guardado correctamente.");

      const { error: perfilError } = await supabase
        .from("perfiles")
        .upsert([Object.fromEntries(
          Object.entries({
            user_id:     user.id,
            nombre:      nombre.trim()  || undefined,
            telefono:    cleanPhone     || undefined,
            role:        "cliente",
            rol:         "cliente",
            modo_activo: "cliente",
            active_mode: "cliente",
            email:       correo.trim()  || undefined,
            correo:      correo.trim()  || undefined,
            updated_at:  new Date().toISOString(),
          }).filter(([, v]) => v !== undefined && v !== null && v !== "")
        )], { onConflict: "user_id" });

      if (perfilError) console.warn("Perfil principal no actualizado:", perfilError.message);
    } catch (error) {
      console.warn("Error guardando perfil cliente:", error?.message || error);
      setErr(error?.message || "No se pudo guardar el perfil de cliente.");
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
        <TopNav setScreen={setScreen} back="landing" backLabel="Inicio" title="Cliente"/>
        <div style={{maxWidth:640,margin:"0 auto",padding:"20px"}}>
          <EmptyBox icon="🔐" title="Inicia sesión" desc="Para registrar tu perfil de cliente necesitas entrar a tu cuenta."/>
          <div style={{height:12}}/>
          <Btn full color={C.blue} onClick={()=>setScreen("cliente-login")}>Entrar o crear cuenta</Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:100}}>
      <TopNav setScreen={setScreen} back="cuenta" backLabel="Cuenta" title="Perfil cliente"/>

      <div style={{maxWidth:640,margin:"0 auto",padding:"20px 20px 0"}}>
        <div style={{background:`linear-gradient(135deg,${C.blue},#123E73)`,borderRadius:22,padding:22,color:"#fff",marginBottom:16,boxShadow:"0 6px 22px rgba(37,88,168,.25)"}}>
          <div style={{fontSize:32,marginBottom:8}}>🏠</div>
          <div style={{fontFamily:"Georgia, serif",fontSize:25,fontWeight:900,marginBottom:4}}>
            Perfil de cliente
          </div>
          <div style={{fontSize:13,opacity:.85,lineHeight:1.5}}>
            Este perfil queda ligado a tu cuenta. Aquí vivirán tus trabajos, autorizaciones, reseñas y mediaciones.
          </div>
        </div>

        {loading ? (
          <div style={{textAlign:"center",padding:"42px 0",color:C.muted}}>Cargando perfil...</div>
        ) : (
          <>
          {/* FIX EXTRA: Foto de perfil del cliente — editable sin borrar la existente */}
          <Card>
            <div style={{color:C.sub,fontSize:11,fontWeight:700,letterSpacing:.8,textTransform:"uppercase",marginBottom:12}}>Foto de perfil</div>
            <div style={{display:"flex",alignItems:"center",gap:16}}>
              <div style={{width:70,height:70,borderRadius:20,overflow:"hidden",background:C.faint,flexShrink:0,border:`2px solid ${C.border}`}}>
                {fotoPreview ? (
                  <img src={fotoPreview} alt="Perfil" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                ) : (
                  <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,color:C.muted}}>👤</div>
                )}
              </div>
              <div style={{flex:1}}>
                <input
                  type="file"
                  accept="image/*"
                  id="ficio-cliente-foto"
                  style={{display:"none"}}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setFotoFile(file);
                    setFotoPreview(URL.createObjectURL(file));
                  }}
                />
                <label htmlFor="ficio-cliente-foto"
                  style={{display:"inline-block",background:C.blueL,border:`1.5px solid ${C.blue}30`,borderRadius:12,padding:"10px 18px",color:C.blue,fontWeight:700,fontSize:13,cursor:"pointer"}}>
                  {fotoPreview ? "📷 Cambiar foto" : "📷 Subir foto"}
                </label>
                {fotoFile && <div style={{color:C.green,fontSize:11,marginTop:6,fontWeight:600}}>✓ Lista para guardar — toca "Guardar perfil"</div>}
                {fotoActual && !fotoFile && <div style={{color:C.muted,fontSize:11,marginTop:6}}>Foto actual guardada. Toca para cambiarla.</div>}
              </div>
            </div>
          </Card>

          <Card>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <Field label="Nombre" value={nombre} onChange={setNombre} placeholder="Nombre completo"/>
              <Field label="WhatsApp" value={telefono} onChange={setTelefono} placeholder="668..."/>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
              <Field label="Correo" value={correo} onChange={setCorreo} placeholder="correo@ejemplo.com"/>
              <Field label="Ciudad" value={zona} onChange={setZona} placeholder="Ej. Los Mochis"/>
            </div>
            <div style={{marginBottom:10}}>
              <Field label="Dirección" value={direccion} onChange={setDireccion} placeholder="Opcional"/>
            </div>

            {err && <div style={{background:C.redL,border:`1px solid ${C.red}35`,borderRadius:12,padding:"10px 12px",color:C.red,fontSize:12,marginBottom:12}}>{err}</div>}
            {msg && <div style={{background:C.greenL,border:`1px solid ${C.green}35`,borderRadius:12,padding:"10px 12px",color:C.green,fontSize:12,marginBottom:12}}>{msg}</div>}

            <Btn full color={C.blue} onClick={guardar}>{saving ? "Guardando..." : "Guardar perfil de cliente"}</Btn>
          </Card>
          </>
        )}

        <Card>
          <div style={{fontFamily:"Georgia, serif",fontSize:18,fontWeight:900,color:C.text,marginBottom:6}}>
            Reputación como cliente
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <InfoMini label="Calificación" value={cliente?.calificacion ?? 5}/>
            <InfoMini label="Trabajos" value={cliente?.trabajos ?? 0}/>
            <InfoMini label="Reseñas" value={cliente?.resenas ?? 0}/>
            <InfoMini label="Estado" value={cliente?.status || "activo"}/>
          </div>
        </Card>

        <Card>
          <div style={{fontFamily:"Georgia, serif",fontSize:18,fontWeight:900,color:C.text,marginBottom:6}}>
            Acciones del cliente
          </div>
          <div style={{color:C.sub,fontSize:13,lineHeight:1.5,marginBottom:14}}>
            Desde aquí podrás buscar trabajadores y consultar el historial de trabajos ligado a tu cuenta.
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Btn full color={C.blue} onClick={()=>setScreen("directory")}>🔍 Buscar maestro</Btn>
            <Btn full outline color={C.green} onClick={()=>setScreen("portal-cliente")}>📋 Mis trabajos</Btn>
          </div>
        </Card>
      </div>
    </div>
  );
};
